import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import './index.less';

import IbPopover from '../common/IbPopover';
import IbTypography from '../common/IbTypography';
import IbIcon from '../common/IbIcon';
import IbAvatar, { IbAvatarSize } from '../common/IbAvatar';
import IbDivider from '../common/IbDivider';
import { IUserProfile } from '../../utils/oidcUtil';
import { formatShortName } from '../../utils/stringUtil';

const MAIN_CLASS_NAME = 'ib-account-widget';
const CONTENT_CLASS_NAME = `${MAIN_CLASS_NAME}__content`;
const USER_INFO_CLASS_NAME = `${CONTENT_CLASS_NAME}__user-info`;
const USER_INFO_POPOVER_CLASS_NAME = `${MAIN_CLASS_NAME}__user-info-popover`;
const USER_INFO_POPOVER_CONTENT_CLASS_NAME = `${USER_INFO_POPOVER_CLASS_NAME}__content`;
const USER_INFO_POPOVER_CONTENT_INFO_AREA_CLASS_NAME = `${USER_INFO_POPOVER_CONTENT_CLASS_NAME}__info-area`;
const USER_INFO_POPOVER_CONTENT_INFO_AREA_TEXT_PART_CLASS_NAME = `${USER_INFO_POPOVER_CONTENT_INFO_AREA_CLASS_NAME}__text-part`;
const USER_INFO_POPOVER_CONTENT_BUTTONS_AREA_CLASS_NAME = `${USER_INFO_POPOVER_CONTENT_CLASS_NAME}__buttons-area`;

export interface IIbAccountWidgetProps {
  currentUser?: IUserProfile;
  onLogout?: () => Promise<void>;
}

const IbAccountWidget: React.FC<IIbAccountWidgetProps> = ({ currentUser, onLogout }) => {
  const { t } = useTranslation();

  const [userInfoPopoverVisible, setUserInfoPopoverVisible] = useState(false);

  const onUserInfoPopoverVisibleChange = (visible?: boolean) => setUserInfoPopoverVisible(visible || false);

  const onEditAccountClick = () => {
    setUserInfoPopoverVisible(false);
  };

  const renderUserInfoContent = () => {
    return (
      <div className={USER_INFO_POPOVER_CONTENT_CLASS_NAME}>
        <div className={USER_INFO_POPOVER_CONTENT_INFO_AREA_CLASS_NAME}>
          <IbAvatar
            metadata={{
              uid: currentUser?.userId || '',
              text: currentUser?.userName || '',
            }}
            size="medium"
          />
          <div className={USER_INFO_POPOVER_CONTENT_INFO_AREA_TEXT_PART_CLASS_NAME}>
            <IbTypography.Paragraph strong type="secondary">
              {currentUser?.userName || ''}
            </IbTypography.Paragraph>
            <IbTypography.Paragraph disabled type="secondary">
              {currentUser?.email || ''}
            </IbTypography.Paragraph>
          </div>
        </div>
        <IbDivider orientation="center" type="horizontal" />
        <div className={USER_INFO_POPOVER_CONTENT_BUTTONS_AREA_CLASS_NAME}>
          <ul>
            <li key={0}>
              <div onClick={onEditAccountClick}>
                <IbIcon iconName="setting-two" size={20} />
                <span>{t('Account settings')}</span>
              </div>
            </li>
            <li key={1}>
              <div>
                <IbIcon iconName="help" size={20} />
                <span>{t('Help')}</span>
              </div>
            </li>
            <li key={2}>
              <div onClick={onLogout}>
                <IbIcon iconName="logout" size={20} />
                <span>{t('Log out')}</span>
              </div>
            </li>
          </ul>
        </div>
      </div>
    );
  };

  const renderAvatar = (size: IbAvatarSize) => (
    <IbAvatar
      metadata={{
        uid: currentUser?.userId || '',
        text: currentUser?.userName || '',
      }}
      size={size}
    />
  );

  const getShortUserName = () => {
    const userName = currentUser?.userName || '';
    const parts = userName.trim().split(/\s+/);
    return formatShortName(parts[0], parts[1], parts[2]);
  };

  const renderUserInfo = () => (
    <div className={USER_INFO_CLASS_NAME}>
      <IbTypography.Paragraph strong type="secondary">
        {getShortUserName()}
      </IbTypography.Paragraph>
      <IbTypography.Paragraph disabled type="descriptor">
        {currentUser?.email || ''}
      </IbTypography.Paragraph>
    </div>
  );

  return (
    <div className={MAIN_CLASS_NAME}>
      <IbPopover
        className={USER_INFO_POPOVER_CLASS_NAME}
        content={renderUserInfoContent()}
        placement="rightBottom"
        trigger="click"
        visible={userInfoPopoverVisible}
        onVisibleChange={onUserInfoPopoverVisibleChange}
      >
        <div className={CONTENT_CLASS_NAME}>
          {renderAvatar('medium')}
          {renderAvatar('x-small')}
          {renderUserInfo()}
        </div>
      </IbPopover>
    </div>
  );
};

export default IbAccountWidget;
